import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import StarField from "@/components/StarField";
import { Trophy, Route, BookOpen, Sparkles, LogIn, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    (async () => {
      setBusy(true);
      try {
        const [s, r] = await Promise.all([
          axios.get(`${API}/me/stats`),
          axios.get(`${API}/me/game-runs?limit=15`),
        ]);
        setStats(s.data);
        setRuns(r.data);
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading]);

  if (loading) {
    return (
      <main
        data-testid="profile-loading"
        className="relative min-h-screen flex items-center justify-center px-6"
      >
        <Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" />
      </main>
    );
  }

  if (!user) {
    return (
      <main
        data-testid="profile-signin-gate"
        className="relative min-h-screen pt-32 px-6 overflow-hidden"
      >
        <StarField count={60} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]" />
        <div className="relative z-10 max-w-md mx-auto rts-glass rounded-2xl p-10 text-center">
          <p className="font-mono-rts text-[0.7rem] tracking-[0.35em] uppercase text-[#EAB308]">
            Your journey
          </p>
          <h1 className="mt-3 font-serif-rts text-3xl md:text-4xl font-light">
            Sign in to remember the road
          </h1>
          <p className="mt-4 text-white/60">
            Track every mile, every verse you unlock, every heaven you reach.
          </p>
          <button
            onClick={signIn}
            className="mt-8 rts-btn-primary w-full"
            data-testid="profile-signin-btn"
          >
            <LogIn className="w-4 h-4" /> Sign in with Google
          </button>
          <button
            onClick={() => navigate("/")}
            className="mt-4 text-xs text-white/40 hover:text-white/70 uppercase tracking-[0.22em]"
          >
            Not now
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="profile-page"
      className="relative min-h-screen pt-28 pb-16 px-6 md:px-10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-5 mb-12"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              referrerPolicy="no-referrer"
              className="w-16 h-16 rounded-full border border-[#EAB308]/40 object-cover"
            />
          ) : (
            <span className="w-16 h-16 rounded-full bg-[#EAB308]/15 text-[#EAB308] font-serif-rts text-2xl flex items-center justify-center">
              {user.name?.[0] || "T"}
            </span>
          )}
          <div>
            <p className="font-mono-rts text-[0.65rem] tracking-[0.35em] uppercase text-[#EAB308]">
              Traveler
            </p>
            <h1
              className="mt-1 font-serif-rts text-3xl md:text-4xl font-light"
              data-testid="profile-name"
            >
              {user.name}
            </h1>
            <p className="text-white/50 text-sm">{user.email}</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12" data-testid="profile-stats">
          <StatBox
            icon={<Route className="w-4 h-4" />}
            label="Runs"
            value={stats?.total_runs ?? 0}
          />
          <StatBox
            icon={<Trophy className="w-4 h-4" />}
            label="Heavens"
            value={stats?.reached_heaven ?? 0}
            accent
          />
          <StatBox
            icon={<Sparkles className="w-4 h-4" />}
            label="Best distance"
            value={(stats?.best_distance ?? 0).toLocaleString()}
          />
          <StatBox
            icon={<Route className="w-4 h-4" />}
            label="Miles driven"
            value={(stats?.total_distance ?? 0).toLocaleString()}
          />
          <StatBox
            icon={<BookOpen className="w-4 h-4" />}
            label="Verses heard"
            value={stats?.total_verses ?? 0}
          />
        </div>

        <p className="font-mono-rts text-[0.68rem] tracking-[0.35em] uppercase text-[#EAB308] mb-4">
          Recent runs
        </p>
        {busy ? (
          <div className="rts-glass rounded-xl p-10 text-center text-white/50">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading your road...
          </div>
        ) : runs.length === 0 ? (
          <div
            className="rts-glass rounded-xl p-10 text-center text-white/60"
            data-testid="profile-empty"
          >
            You haven't driven yet. Grace is patient — the wheel is waiting.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="profile-runs-list">
            {runs.map((r) => (
              <li
                key={r.id}
                className="rts-glass rounded-lg px-5 py-4 flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      r.reached_heaven ? "bg-[#EAB308]" : "bg-white/40"
                    }`}
                  />
                  <span className="font-serif-rts text-lg">
                    {r.distance.toLocaleString()} <span className="text-white/40">/ 3,000</span>
                  </span>
                  {r.reached_heaven && (
                    <span className="text-[0.62rem] uppercase tracking-[0.25em] text-[#EAB308]">
                      Heaven
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-white/50 font-mono-rts text-[0.68rem] uppercase tracking-[0.2em]">
                  <span>{r.verses_seen} verses</span>
                  <span>{formatDate(r.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

function StatBox({ icon, label, value, accent }) {
  return (
    <div className="rts-glass rounded-xl p-5">
      <div className={`flex items-center gap-2 ${accent ? "text-[#EAB308]" : "text-white/50"}`}>
        {icon}
        <span className="font-mono-rts text-[0.6rem] tracking-[0.3em] uppercase">
          {label}
        </span>
      </div>
      <p
        className={`mt-3 font-serif-rts text-3xl font-light ${
          accent ? "text-[#EAB308]" : "text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function formatDate(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
